import { CancelSmIcon } from "cssReset/icon";
import { ChangeEvent, useEffect, useState } from "react";
import { UseFormRegister, UseFormSetValue } from "react-hook-form";
import styled from "styled-components";

type AttachmentFile = File;

interface FileUploadProps {
  name: string;
  register: UseFormRegister<any>;
  setValue: UseFormSetValue<any>;
  initialFiles?: AttachmentFile[]; // 초기 파일 목록
}

export function AdminCalibrationAttachment({
  register,
  setValue,
  name,
  initialFiles = [],
}: FileUploadProps) {
  const [files, setFiles] = useState<AttachmentFile[]>(initialFiles); // File 또는 ReviewFile 관리
  const [previews, setPreviews] = useState<string[]>([]); // 초기에는 빈 배열

  useEffect(() => {
    setValue(name, files); // 초기 파일 목록 설정
  }, [files, name, setValue]);

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = event.target.files;
    if (selectedFiles) {
      const newFiles = Array.from(selectedFiles);

      // 최대 1개 파일 제한
      if (files.length + newFiles.length > 1) {
        alert("최대 1개의 파일만 첨부할 수 있습니다.");
        return;
      }

      const updatedFiles = [...files, ...newFiles];
      const updatedPreviews = [
        ...previews,
        ...newFiles.map((file) => URL.createObjectURL(file)),
      ];

      setFiles(updatedFiles);
      setPreviews(updatedPreviews);
      setValue(name, updatedFiles); // useForm에 파일 목록 설정
    }
  };

  const handleRemoveFile = (index: number) => {
    const updatedFiles = files.filter((_, i) => i !== index);
    const updatedPreviews = previews.filter((_, i) => i !== index);

    setFiles(updatedFiles);
    setPreviews(updatedPreviews);
    setValue(name, updatedFiles); // useForm에 업데이트된 파일 목록 반영
  };

  return (
    <FileAttachWrap>
      <PreviewContainer>
        {files.length > 0 && (
          <div className="preview-item-wrapper">
            {previews.map((preview, index) => (
              <div key={index} className="preview-item">
                <div style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "100%",
                  width: "100%",
                  fontSize: "2.4rem"
                }}>
                  <span role="img" aria-label="텍스트 파일">📄</span>
                  <span style={{
                    fontSize: "1.2rem",
                    marginTop: "0.5rem",
                    wordBreak: "break-all",
                    textAlign: "center"
                  }}>
                    {files[index].name}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => handleRemoveFile(index)}
                  className="remove-button"
                >
                  <CancelSmIcon />
                </button>
              </div>
            ))}
          </div>
        )}
      </PreviewContainer>
      <div className="file-box">
        <label className="file-attach-box">
          <span>캘리브레이션 파일 업로드</span>
          <input
            type="file"
            multiple // 여러 파일 선택 가능
            accept="text/plain" // ✅ 텍스트 파일만 허용
            onChange={(e) => {
              handleFileChange(e);
              register(name).onChange(e); // register의 onChange 핸들러 호출
            }}
          />
        </label>
        <span className="file-limits">
          파일형식 TXT
        </span>
      </div>
    </FileAttachWrap>
  );
};

const FileAttachWrap = styled.div`
  width: 100%;
  background-color: white;
  display: flex;
  flex-direction: column;
  gap: 2.4rem;
  .file-box {
    display: flex;
    gap: 1rem;
    align-items: center;
    .file-attach-box {
      display: flex;
      align-items: center;
      gap: 0.4rem;
      border: 1px solid #cecece;
      box-shadow: 1px 1px 4px 0 #00000040;
      padding: 1rem;
      border-radius: 0.5rem;
      cursor: pointer;
      span {
        font-size: 1.4rem;
        font-weight: 400;
        line-height: 2rem;
        letter-spacing: -0.06rem;
      }
      input[type="file"] {
        display: none;
      }
    }
    .file-limits {
      font-weight: 400;
      font-size: 1.6rem;
      line-height: 2.4rem;
    }
  }
`;

const PreviewContainer = styled.div`
  .preview-item-wrapper {
    display: flex;
    align-items: center;
    gap: 1rem;
    padding: 1.6rem 0.8rem;
    overflow: scroll hidden;
    border-bottom: 1px solid #a1a1a1;
    /* ( 크롬, 사파리, 오페라, 엣지 ) 동작 */
    &::-webkit-scrollbar {
      display: none;
    }
    .preview-item {
      width: 8rem;
      height: 8rem;
      position: relative;
      flex: 0 0 auto;
      img {
        max-width: 100%;
        overflow: hidden;
        object-fit: cover;
        border-radius: 0.5rem;
        width: 100%;
        height: 100%;
      }
      button {
        border: 0;
        outline: 0;
        width: 2rem;
        height: 2rem;
        background-color: transparent;
        padding: 0;
        display: flex;
        align-items: center;
        justify-content: center;

        position: absolute;
        top: -0.5rem;
        right: -0.5rem;
        svg {
          width: 100%;
          height: 100%;
        }
      }
    }
  }
`;
